import React from 'react';
import Modal from '../../modal/index';
import './email-verification-modal.css';
import { t, getCurrentLanguage } from '../../../utils/translations';
import useLanguageStore from '../../../store/languageStore';
import { GATE_BASE_URL, gateFetchOptions } from '../gate-api';

class EmailVerificationModal2610 extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      token: '',
      isLoading: false,
      error: null,
      tokenError: null,
      currentLanguage: getCurrentLanguage(),
      countdown: 900, // 15 minutes in seconds
      canResend: false
    };
    this.countdownInterval = null;
  }

  componentDidMount() {
    if (this.props.isOpen) {
      this.startCountdown();
    }
    
    // Subscribe to language changes
    this.unsubscribe = useLanguageStore.subscribe((state) => {
      this.setState({ currentLanguage: state.currentLanguage });
    });
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.startCountdown();
      // Reset form when modal opens
      this.setState({
        token: '',
        error: null,
        tokenError: null,
        countdown: 900,
        canResend: false
      });
    }
    
    if (!this.props.isOpen && prevProps.isOpen) {
      this.stopCountdown();
    }
  }

  componentWillUnmount() {
    this.stopCountdown();
    
    // Clean up language subscription
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  startCountdown = () => {
    this.stopCountdown(); // Clear any existing countdown
    
    this.countdownInterval = setInterval(() => {
      this.setState(prevState => {
        const newCountdown = prevState.countdown - 1;
        
        if (newCountdown <= 0) {
          return {
            countdown: 0,
            canResend: true
          };
        }
        
        return {
          countdown: newCountdown
        };
      });
    }, 1000);
  };

  stopCountdown = () => {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  };

  formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  handleTokenChange = (event) => {
    const token = event.target.value.replace(/\D/g, '').slice(0, 6); // Only digits, max 6
    this.setState({ 
      token,
      tokenError: null // Clear token error when user types
    });
  };

  handleSubmit = async (event) => {
    event.preventDefault();
    
    const { token } = this.state;
    const { email } = this.props;
    
    // Validate token
    if (!token.trim()) {
      this.setState({ tokenError: t('token_required_error', this.state.currentLanguage) });
      return;
    }
    
    if (token.length !== 6) {
      this.setState({ tokenError: t('token_invalid_length_error', this.state.currentLanguage) });
      return;
    }
    
    this.setState({ isLoading: true, error: null, tokenError: null });
    
    try {
      // Same-origin: this response carries the visitor cookie, and a Lax cookie
      // set by a cross-site response is dropped by the browser. See gate-api.js.
      const response = await fetch(`${GATE_BASE_URL}/verify-email`, {
        method: 'POST',
        ...gateFetchOptions,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: email.trim(),
          token: token.trim()
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Track successful verification
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'email_verified',
            event_category: 'User Registration',
            event_action: 'Email Verified',
            event_label: 'Token Verification Success',
            current_language: window.current_language || 'pt',
            user_email_domain: email.split('@')[1]
          });
        }
        
        // Call success callback
        if (this.props.onVerificationSuccess) {
          await this.props.onVerificationSuccess({ email, registrationData: data });
        }
        
        // Close the modal
        this.props.onClose();
        
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        
        // Track verification failure
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'email_verification_failed',
            event_category: 'User Registration',
            event_action: 'Email Verification Failed',
            event_label: errorData.detail || 'Token Verification Error',
            current_language: window.current_language || 'pt'
          });
        }
        
        this.setState({ 
          error: errorData.detail || t('verification_failed_error', this.state.currentLanguage),
          isLoading: false 
        });
      }
      
    } catch (error) {
      console.error('❌ Error verifying token:', error);
      this.setState({ 
        error: t('verification_error', this.state.currentLanguage),
        isLoading: false 
      });
    }
  };

  handleResendCode = async () => {
    if (!this.state.canResend) return;
    
    this.setState({ isLoading: true, error: null });
    
    try {
      // Same-origin: the resend can auto-register an address verified in the last
      // 24 hours, and that branch also sets the visitor cookie.
      const response = await fetch(`${GATE_BASE_URL}/send-verification`, {
        method: 'POST',
        ...gateFetchOptions,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: this.props.email.trim(),
          language: this.state.currentLanguage || 'pt'
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.auto_registered) {
          // User was auto-registered
          if (this.props.onVerificationSuccess) {
            await this.props.onVerificationSuccess({ 
              email: this.props.email, 
              registrationData: data,
              autoRegistered: true 
            });
          }
          this.props.onClose();
        } else {
          // New code sent, restart countdown
          this.setState({
            countdown: 900,
            canResend: false,
            error: null,
            isLoading: false
          });
          this.startCountdown();
        }
      } else {
        this.setState({ 
          error: t('resend_failed_error', this.state.currentLanguage),
          isLoading: false 
        });
      }
    } catch (error) {
      console.error('❌ Error resending code:', error);
      this.setState({ 
        error: t('resend_network_error', this.state.currentLanguage),
        isLoading: false 
      });
    }
  };

  handleClose = () => {
    this.stopCountdown();
    
    // Reset form state when closing
    this.setState({
      token: '',
      isLoading: false,
      error: null,
      tokenError: null,
      countdown: 900,
      canResend: false
    });
    
    this.props.onClose();
  };

  render() {
    const { isOpen, email } = this.props;
    const { token, isLoading, error, tokenError, currentLanguage, countdown, canResend } = this.state;

    if (!isOpen) return null;

    return (
      <Modal isOpen={isOpen} onClose={this.handleClose}>
        <div className="email-verification-modal">
          <div className="verification-header">
            <h2>{t('email_verification_title', currentLanguage)}</h2>
            <p>{t('email_verification_description', currentLanguage)}</p>
            <p className="email-display">
              <strong>{email}</strong>
            </p>
          </div>

          <form onSubmit={this.handleSubmit} className="verification-form">
            {/* Token Input */}
            <div className="form-group">
              <label htmlFor="token" className="form-label">
                {t('verification_code_label', currentLanguage)} <span className="required">*</span>
              </label>
              <input
                type="text"
                id="token"
                name="token"
                value={token}
                onChange={this.handleTokenChange}
                placeholder={t('verification_code_placeholder', currentLanguage)}
                className={`form-input token-input ${tokenError ? 'error' : ''}`}
                disabled={isLoading}
                maxLength="6"
                autoComplete="one-time-code"
                required
              />
              {tokenError && (
                <span className="error-message">{tokenError}</span>
              )}
            </div>

            {/* Countdown and Resend */}
            <div className="verification-info">
              {countdown > 0 ? (
                <p className="countdown-text">
                  {t('code_expires_in', currentLanguage)} <strong>{this.formatTime(countdown)}</strong>
                </p>
              ) : (
                <p className="expired-text">
                  {t('code_expired', currentLanguage)}
                </p>
              )}
              
              <button
                type="button"
                className={`resend-button ${canResend ? 'active' : 'disabled'}`}
                onClick={this.handleResendCode}
                disabled={!canResend || isLoading}
              >
                {t('resend_code_button', currentLanguage)}
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="error-message-box">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <div className="form-actions">
              <button
                type="submit"
                className="verify-button"
                disabled={isLoading || !token.trim() || token.length !== 6}
              >
                {isLoading ? (
                  <>
                    <span className="loading-spinner"></span>
                    {t('verifying_button', currentLanguage)}
                  </>
                ) : (
                  t('verify_email_button', currentLanguage)
                )}
              </button>
            </div>
          </form>

          <div className="verification-footer">
            <p className="help-text">
              <small>
                {t('verification_help_text', currentLanguage)}
              </small>
            </p>
          </div>
        </div>
      </Modal>
    );
  }
}

export default EmailVerificationModal2610;
